"use client";

import { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useBranding } from "@/components/branding-provider";

type Step = "credentials" | "totp" | "company";

/** Empresa ofrecida por el login multi-empresa (membresías). */
type LoginCompany = { id: string; name: string; slug: string; role: string };

const COMPANY_ROLE_LABEL: Record<string, string> = {
  TENANT_ADMIN: "Administrador",
  SUPERVISOR: "Supervisor",
  AGENT: "Agente",
};

/* Tipografías de la identidad OmniDialer (cargadas en el layout). */
const UI = {
  fontFamily: "var(--font-brand-ui), 'IBM Plex Sans', system-ui, sans-serif",
} as const;
const DISPLAY =
  "var(--font-brand-display), 'Schibsted Grotesk', system-ui, sans-serif";
/* Colores de marca del workspace (inyectados por tenant en :root). */
const BRAND = "var(--color-primary)";
/* Mezcla un color de marca con transparente/base para sombras y fondos. */
const mix = (color: string, pct: number, base = "transparent") =>
  `color-mix(in srgb, ${color} ${pct}%, ${base})`;
/* Bases del acceso: clara para el lado del formulario, oscura para la escena.
   Ambas son neutras a propósito — el tinte lo pone el color del tenant. */
const LIGHT = "#f4f7fb";
const DARK = "#070d1a";

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginInner />
    </Suspense>
  );
}

function LoginInner() {
  const router = useRouter();
  const params = useSearchParams();
  const { appName, logoUrl } = useBranding();

  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpDigits, setTotpDigits] = useState<string[]>(Array(6).fill(""));
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paso "company": elección de empresa cuando el usuario tiene membresías.
  const [ticket, setTicket] = useState<string | null>(null);
  const [companies, setCompanies] = useState<LoginCompany[]>([]);
  const [companyLoading, setCompanyLoading] = useState<string | null>(null);
  const [ticketExpired, setTicketExpired] = useState(false);

  function nextFor(
    role: "PLATFORM_ADMIN" | "TENANT_ADMIN" | "SUPERVISOR" | "AGENT"
  ): string {
    if (role === "PLATFORM_ADMIN") return "/platform";
    const requested = params.get("next");
    const defaultPath = role === "AGENT" ? "/agent" : "/admin";
    if (!requested) return defaultPath;
    if (role === "AGENT" && requested.startsWith("/agent")) return requested;
    // Admin/supervisor: además de /admin, puede pedir /agent (vista de agente).
    if (
      role !== "AGENT" &&
      (requested.startsWith("/admin") || requested.startsWith("/agent"))
    )
      return requested;
    return defaultPath;
  }

  const totpCode = totpDigits.join("");

  // `codeOverride`: el auto-submit del paso TOTP dispara `submit` desde el
  // onChange del último dígito, cuando `totpCode` (derivado de `totpDigits`)
  // todavía es el del render ANTERIOR — le faltaría ese dígito. El onChange
  // pasa el código completo aquí para no depender del closure.
  async function submit(
    e?: React.FormEvent<HTMLFormElement>,
    codeOverride?: string
  ) {
    e?.preventDefault();
    setError(null);
    setLoading(true);
    try {
      let emailVal = email;
      let passwordVal = password;
      const form =
        e?.currentTarget ??
        (document.getElementById("login-form") as HTMLFormElement | null);
      if (form && step === "credentials") {
        const fd = new FormData(form);
        emailVal = ((fd.get("email") as string) || email).trim();
        passwordVal = (fd.get("password") as string) || password;
        if (emailVal !== email) setEmail(emailVal);
        if (passwordVal !== password) setPassword(passwordVal);
      }

      if (step === "credentials" && (!emailVal || !passwordVal)) {
        setError("Ingresa tu correo o extensión y la contraseña");
        return;
      }

      const body: Record<string, string> = {
        email: emailVal,
        password: passwordVal,
      };
      if (step === "totp") body.totpCode = codeOverride ?? totpCode;

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : Array.isArray(data?.message)
              ? data.message.join(", ")
              : "No se pudo iniciar sesión";
        setError(msg);
        return;
      }

      if (data.requiresTotp) {
        setStep("totp");
        return;
      }

      if (data.requiresTenant) {
        setTicket(data.ticket);
        setCompanies(Array.isArray(data.companies) ? data.companies : []);
        setTicketExpired(false);
        setStep("company");
        return;
      }

      const role = data?.user?.role as
        | "PLATFORM_ADMIN"
        | "TENANT_ADMIN"
        | "SUPERVISOR"
        | "AGENT"
        | undefined;
      const dest = role
        ? nextFor(role)
        // Caso de reserva por si la respuesta llegara sin rol: el middleware
        // reencamina igualmente (un AGENT que pise /admin acaba en /agent), así
        // que no hace falta que el usuario declare quién es al entrar.
        : params.get("next") || "/admin";
      router.push(dest);
      router.refresh();
    } catch {
      setError("Error de red. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  /** Regresa al paso de credenciales limpiando el estado de los demás pasos. */
  function backToCredentials() {
    setStep("credentials");
    setTotpDigits(Array(6).fill(""));
    setTicket(null);
    setCompanies([]);
    setCompanyLoading(null);
    setTicketExpired(false);
    setError(null);
  }

  /** Canjea el ticket del login por la sesión en la empresa elegida. */
  async function chooseCompany(tenantId: string) {
    if (companyLoading) return;
    setError(null);
    setCompanyLoading(tenantId);
    try {
      const res = await fetch("/api/auth/login/tenant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticket, tenantId }),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          typeof data?.message === "string"
            ? data.message
            : Array.isArray(data?.message)
              ? data.message.join(", ")
              : "No se pudo entrar a la empresa";
        setError(msg);
        // Ticket caducado o inválido: hay que volver a poner credenciales.
        if (res.status === 401 || res.status === 410) setTicketExpired(true);
        return;
      }

      const role = data?.user?.role as
        | "PLATFORM_ADMIN"
        | "TENANT_ADMIN"
        | "SUPERVISOR"
        | "AGENT"
        | undefined;
      router.push(role ? nextFor(role) : "/admin");
      router.refresh();
    } catch {
      setError("Error de red. Intenta de nuevo.");
    } finally {
      setCompanyLoading(null);
    }
  }

  const inputCls =
    "w-full px-[15px] py-[13px] rounded-md border-[1.5px] border-[#dbe2ec] bg-white text-[14.5px] text-[#0f172a] outline-none transition-all focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-primary)_15%,transparent)] placeholder:text-[#94a3b8]";

  return (
    <div className="min-h-screen flex" style={UI}>
      {/* ── Izquierda: el formulario ──
          Se lleva algo más de la mitad en pantallas anchas y ocupa el ancho
          completo por debajo de lg, donde la escena se oculta. */}
      <div
        className="flex-1"
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
          overflow: "hidden",
          background: `linear-gradient(160deg, ${mix(BRAND, 9, LIGHT)} 0%, ${LIGHT} 60%, ${mix(BRAND, 5, LIGHT)} 100%)`,
        }}
      >
        {/* Un resplandor muy tenue a la deriva: da profundidad al lado claro
            sin competir con la escena de la derecha, que es la protagonista. */}
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            // Más ancho y mucho más tenue que antes (iba al 20%): a esa
            // intensidad se leía como una mancha azul en la esquina en vez de
            // como luz. Ensancharlo y bajarlo al 8% lo convierte en ambiente.
            width: 700,
            height: 700,
            left: "-26%",
            top: "-22%",
            background: `radial-gradient(circle, ${mix(BRAND, 8)}, transparent 72%)`,
            animation: "zdl-drift 24s ease-in-out infinite",
          }}
        />
        <div
          className="animate-fade-in-up"
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* Marca centrada. Estilos en línea porque Tailwind no compila las
              clases nuevas de este archivo (ver nota en globals.css). */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              alignSelf: "center",
            }}
          >
            <img
              src={logoUrl || "/omnidialer-mark.svg"}
              alt={appName}
              style={{
                maxHeight: 52,
                maxWidth: 220,
                objectFit: "contain",
              }}
            />
            {/* Nombre junto al logo. Va siempre: ni el logo del tenant ni el
                isotipo de OmniDialer llevan el wordmark dentro. */}
            <span
              style={{
                fontFamily: DISPLAY,
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: "#0f172a",
              }}
            >
              {appName}
            </span>
          </div>

          {step === "credentials" ? (
            <form
              id="login-form"
              onSubmit={submit}
              noValidate
              className="flex flex-col gap-7 animate-fade-in-up"
            >
              <div className="flex flex-col gap-1.5">
                <h1
                  className="text-[27px] font-bold text-[#0f172a] leading-tight tracking-tight"
                  style={{ fontFamily: DISPLAY }}
                >
                  Inicia sesión
                </h1>
                <p className="text-[14px] text-[#64748b]">
                  Accede a tu espacio de trabajo.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-[7px]">
                  <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748b]">
                    Correo o extensión
                  </label>
                  <input
                    name="email"
                    type="text"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu.nombre@empresa.com o 1001"
                    className={inputCls}
                  />
                </div>

                <div className="flex flex-col gap-[7px]">
                  <div className="flex justify-between items-baseline">
                    <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748b]">
                      Contraseña
                    </label>
                    <button
                      type="button"
                      onClick={() => alert("Próximamente")}
                      className="text-[12.5px] font-semibold text-[var(--color-primary)] hover:text-[#1d4ed8] hover:underline"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                  <div className="relative flex">
                    <input
                      name="password"
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputCls + " pr-[46px]"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      tabIndex={-1}
                      aria-label={
                        showPwd ? "Ocultar contraseña" : "Mostrar contraseña"
                      }
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 px-2.5 py-2 text-[#94a3b8] hover:text-[var(--color-primary)]"
                    >
                      {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-3.5 py-[11px] rounded-md bg-[#fef2f2] border border-[#fecaca] text-[#b91c1c] text-[13px] font-medium animate-fade-in-up">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{ background: "var(--color-primary)" }}
                  className="mt-1 py-[15px] rounded-xl text-white text-[15.5px] font-bold flex items-center justify-center gap-2.5 shadow-[0_8px_22px_color-mix(in_srgb,var(--color-primary)_35%,transparent)] transition-all hover:brightness-110 hover:shadow-[0_10px_26px_color-mix(in_srgb,var(--color-primary)_38%,transparent)] active:scale-[.985] disabled:opacity-70 disabled:cursor-wait"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  {loading ? "Verificando…" : "Entrar"}
                </button>
              </div>

              <p className="text-center text-[12.5px] text-[#94a3b8]">
                ¿Problemas para acceder?{" "}
                <a
                  href="mailto:soporte@omniia.app"
                  className="text-[var(--color-primary)] font-semibold hover:underline"
                >
                  Contacta a soporte TI
                </a>
              </p>
            </form>
          ) : step === "totp" ? (
            <div className="flex flex-col gap-6 animate-fade-in-up">
              <button
                type="button"
                onClick={() => {
                  setStep("credentials");
                  setTotpDigits(Array(6).fill(""));
                  setError(null);
                }}
                className="inline-flex items-center gap-1 text-[13px] text-[#64748b] hover:text-[var(--color-primary)] self-start"
              >
                <ArrowLeft size={16} /> Volver
              </button>
              <div>
                <h1
                  className="text-[26px] font-bold text-[#0f172a] leading-tight tracking-tight"
                  style={{ fontFamily: DISPLAY }}
                >
                  Verifica tu identidad
                </h1>
                <p className="text-[14.5px] text-[#64748b] mt-1.5">
                  Ingresa el código de 6 dígitos de tu app autenticadora.
                </p>
              </div>

              <TotpInput
                value={totpDigits}
                onChange={(d) => {
                  setTotpDigits(d);
                  if (d.every((c) => c) && !loading) {
                    // Pasa el código completo explícito (no depender del closure).
                    const code = d.join("");
                    setTimeout(() => submit(undefined, code), 50);
                  }
                }}
                disabled={loading}
              />

              {error && (
                <div className="flex items-center gap-2 px-3.5 py-[11px] rounded-md bg-[#fef2f2] border border-[#fecaca] text-[#b91c1c] text-[13px] font-medium">
                  {error}
                </div>
              )}

              <button
                type="button"
                onClick={() => submit()}
                disabled={loading || totpCode.length !== 6}
                style={{ background: "var(--color-primary)" }}
                className="py-[15px] rounded-xl text-white text-[15.5px] font-bold flex items-center justify-center gap-2.5 shadow-[0_8px_22px_color-mix(in_srgb,var(--color-primary)_35%,transparent)] transition-all hover:brightness-110 active:scale-[.985] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                Verificar
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-6 animate-fade-in-up">
              <button
                type="button"
                onClick={backToCredentials}
                className="inline-flex items-center gap-1 text-[13px] text-[#64748b] hover:text-[var(--color-primary)] self-start"
              >
                <ArrowLeft size={16} /> Volver
              </button>
              <div>
                <h1
                  className="text-[26px] font-bold text-[#0f172a] leading-tight tracking-tight"
                  style={{ fontFamily: DISPLAY }}
                >
                  ¿A qué empresa quieres entrar?
                </h1>
                <p className="text-[14.5px] text-[#64748b] mt-1.5">
                  Tu usuario tiene acceso a varias empresas. Elige una para
                  continuar.
                </p>
              </div>

              <div className="flex flex-col gap-2.5">
                {companies.map((c) => (
                  <CompanyButton
                    key={c.id}
                    name={c.name}
                    roleLabel={COMPANY_ROLE_LABEL[c.role] ?? c.role}
                    loading={companyLoading === c.id}
                    disabled={!!companyLoading || ticketExpired}
                    onClick={() => chooseCompany(c.id)}
                  />
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 px-3.5 py-[11px] rounded-md bg-[#fef2f2] border border-[#fecaca] text-[#b91c1c] text-[13px] font-medium">
                  {error}
                </div>
              )}

              {ticketExpired && (
                <button
                  type="button"
                  onClick={backToCredentials}
                  style={{ background: "var(--color-primary)" }}
                  className="py-[15px] rounded-xl text-white text-[15.5px] font-bold flex items-center justify-center gap-2.5 shadow-[0_8px_22px_color-mix(in_srgb,var(--color-primary)_35%,transparent)] transition-all hover:brightness-110 active:scale-[.985]"
                >
                  Volver a iniciar sesión
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Derecha: escena de llamadas ── */}
      <EscenaLlamadas />
    </div>
  );
}

/** Contador de llamadas activas. Sube desde cero hasta `base` y a partir de
 *  ahí sigue moviéndose alrededor de esa cifra, como haría un número real: un
 *  valor clavado delata que la escena es un decorado.
 *
 *  El vaivén arranca DESPUÉS de la subida para que no se peleen. Los saltos
 *  son de una o dos llamadas y a veces ninguno —así no late con un ritmo
 *  mecánico— y se quedan dentro de una banda alrededor de `base`, para que no
 *  derive hasta una cifra absurda al cabo de un rato.
 *
 *  Invisible hasta montar: el servidor pinta el estado inicial, que es cero, y
 *  un "0 llamadas activas" parecería un dato real y alarmante. `visibility`
 *  reserva el hueco para que la línea no dé un salto al aparecer.
 *
 *  `Math.random` solo se usa dentro del efecto, nunca al renderizar: en el
 *  render rompería la hidratación al no coincidir con lo que pintó el servidor. */
function Contador({ base }: { base: number }) {
  const [n, setN] = useState(0);
  const [montado, setMontado] = useState(false);

  useEffect(() => setMontado(true), []);

  useEffect(() => {
    const quieto =
      typeof matchMedia === "function" &&
      matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (quieto) {
      setN(base);
      return;
    }

    const SUBIDA = 2400;
    let raf = 0;
    let inicio = 0;

    const paso = (t: number) => {
      if (!inicio) inicio = t;
      const p = Math.min(1, (t - inicio) / SUBIDA);
      setN(Math.round(base * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(paso);
    };
    raf = requestAnimationFrame(paso);

    // En pestaña de fondo el navegador congela requestAnimationFrame; este
    // temporizador sí corre y deja puesta la cifra de partida.
    const red = setTimeout(() => setN(base), SUBIDA + 600);

    let vaiven: ReturnType<typeof setInterval> | undefined;
    const arranque = setTimeout(() => {
      vaiven = setInterval(() => {
        setN((prev) => {
          // Un tercio de las veces no se mueve: el silencio hace que los
          // cambios se noten más que un parpadeo constante.
          if (Math.random() < 0.34) return prev;
          // El sentido no es 50/50: cuanto más lejos está de `base`, más
          // probable es volver hacia ella. Sin esa atracción, un paseo
          // aleatorio se queda pegado a los topes de la banda durante minutos.
          const desvio = prev - base;
          const prob = Math.min(0.9, Math.max(0.1, 0.5 - desvio * 0.06));
          const signo = Math.random() < prob ? 1 : -1;
          const salto = signo * (Math.random() < 0.75 ? 1 : 2);
          return Math.min(base + 7, Math.max(base - 6, prev + salto));
        });
      }, 1900);
    }, SUBIDA + 400);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(red);
      clearTimeout(arranque);
      if (vaiven) clearInterval(vaiven);
    };
  }, [base]);

  return (
    <span
      style={{
        fontVariantNumeric: "tabular-nums",
        visibility: montado ? "visible" : "hidden",
      }}
    >
      {n}
    </span>
  );
}

/* ── Vídeo de fondo de la escena ──────────────────────────────────────────
   Clip de Pexels (licencia Pexels: uso comercial sin atribución), servido
   desde /agente-llamada.mp4, 2,2 MB en 960×506.

   Decisiones que importan:
   · `muted` + `playsInline` son OBLIGATORIOS para que el navegador permita la
     reproducción automática; sin `muted` la bloquea y se queda el primer
     fotograma congelado.
   · `preload="metadata"` en vez de "auto": el vídeo es decorativo y no debe
     competir por el ancho de banda con el formulario, que es a lo que la
     persona ha venido.
   · No lleva `poster`: mientras carga —o si el navegador se niega a
     reproducir— lo que se ve es el degradado de marca del <aside> que hay
     debajo, que ya es un fondo válido por sí mismo. Una imagen de respaldo
     solo añadiría otra descarga para tapar algo que no está roto.
   · Encima va un velo oscuro: el clip es claro y sin él ni el titular ni el
     texto pasarían el contraste mínimo.
   · Si el vídeo no carga, debajo queda el degradado de marca del <aside>, así
     que la escena nunca se ve rota. */
function VideoLlamada() {
  return (
    <>
      <video
        src="/agente-llamada.mp4"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />
      {/* Velo: oscurece el clip y lo tiñe con el color de marca, para que el
          panel siga perteneciendo a la identidad y no sea "un vídeo pegado". */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          // Dos capas: un tinte de marca muy leve que unifica el clip con la
          // identidad, y encima un degradado que SOLO oscurece arriba y abajo,
          // que es donde caen el estado y el titular. En la franja central el
          // vídeo se ve casi limpio — antes iba al 78-92% y lo tapaba entero.
          background: `linear-gradient(to bottom, rgba(7,13,26,.82) 0%, rgba(7,13,26,.28) 26%, rgba(7,13,26,.22) 52%, rgba(7,13,26,.72) 84%, rgba(7,13,26,.9) 100%), linear-gradient(150deg, ${mix(BRAND, 30, "transparent")} 0%, transparent 55%)`,
        }}
      />
    </>
  );
}

/* ── Escena: una llamada en curso ─────────────────────────────────────────
   Sustituye a un diagrama de nodos que quedaba abstracto y no se leía como
   "llamadas". Esta enseña el producto trabajando —la llamada que el marcador
   acaba de entregar al agente, con su cronómetro, su voz y sus controles— y
   detrás, las que siguen en cola. Todo HTML y CSS; se oculta bajo 1024px. */
function EscenaLlamadas() {
  return (
    <aside
      className="od-login-escena"
      style={{
        flex: "0.92",
        position: "relative",
        overflow: "hidden",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: 52,
        background: `linear-gradient(155deg, ${mix(BRAND, 26, DARK)} 0%, ${DARK} 58%, ${mix(BRAND, 14, DARK)} 100%)`,
      }}
    >
      <VideoLlamada />

      {/* Estado */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: "#22e58a",
            boxShadow: "0 0 10px #22e58a",
            animation: "zdl-blink 2.6s ease-in-out infinite",
          }}
        />
        <span
          style={{
            fontSize: 12,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.85)",
            fontWeight: 600,
            textShadow: "0 2px 12px rgba(7,13,26,.75)",
          }}
        >
          Marcando en tiempo real
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 12.5,
            color: "rgba(255,255,255,.55)",
          }}
        >
          <span style={{ color: "#ffffff", fontWeight: 600 }}>
            <Contador base={32} />
          </span>{" "}
          llamadas activas
        </span>
      </div>

      {/* Copy */}
      <div style={{ position: "relative" }}>
        <h2
          style={{
            fontFamily: DISPLAY,
            color: "#ffffff",
            fontSize: 29,
            fontWeight: 700,
            lineHeight: 1.22,
            letterSpacing: "-0.02em",
            margin: "0 0 10px",
            // Sobre vídeo el brillo del fondo cambia fotograma a fotograma;
            // la sombra sostiene el contraste sin tener que oscurecer más el
            // clip, que es justo lo que se quería evitar.
            textShadow: "0 2px 12px rgba(7,13,26,.75)",
          }}
        >
          Varias llamadas a la vez.
          <br />
          Un agente para cada respuesta.
        </h2>
        <p
          style={{
            color: "rgba(255,255,255,.78)",
            fontSize: 14.5,
            lineHeight: 1.65,
            maxWidth: 400,
            margin: 0,
            textShadow: "0 2px 12px rgba(7,13,26,.75)",
          }}
        >
          El marcador lanza los intentos, descarta buzones y tonos ocupados, y
          solo pasa al agente las conversaciones que alguien ha contestado.
        </p>
      </div>
    </aside>
  );
}

/* ── Tarjeta de empresa (paso "company" del login multi-empresa) ── */
function CompanyButton({
  name,
  roleLabel,
  loading,
  disabled,
  onClick,
}: {
  name: string;
  roleLabel: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-left transition-all border-[1.5px] bg-white disabled:cursor-not-allowed " +
        (loading
          ? "border-[var(--color-primary)] shadow-[0_3px_10px_color-mix(in_srgb,var(--color-primary)_16%,transparent)]"
          : disabled
            ? "border-[#dbe2ec] opacity-60"
            : "border-[#dbe2ec] hover:border-[color-mix(in_srgb,var(--color-primary)_55%,transparent)] hover:shadow-[0_3px_10px_color-mix(in_srgb,var(--color-primary)_16%,transparent)]")
      }
    >
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[15px] font-semibold text-[#0f172a] truncate">
          {name}
        </span>
        <span className="text-[11.5px] text-[#64748b]">{roleLabel}</span>
      </span>
      {loading && (
        <Loader2
          size={18}
          className="animate-spin shrink-0 text-[var(--color-primary)]"
        />
      )}
    </button>
  );
}

/* ── Código TOTP (2FA) ── */
function TotpInput({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const setDigit = useCallback(
    (i: number, raw: string) => {
      const digit = raw.replace(/\D/g, "").slice(-1);
      const copy = [...value];
      copy[i] = digit;
      onChange(copy);
      if (digit && i < 5) refs.current[i + 1]?.focus();
    },
    [value, onChange]
  );

  const onKeyDown =
    (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !value[i] && i > 0) {
        const copy = [...value];
        copy[i - 1] = "";
        onChange(copy);
        refs.current[i - 1]?.focus();
      } else if (e.key === "ArrowLeft" && i > 0) {
        refs.current[i - 1]?.focus();
      } else if (e.key === "ArrowRight" && i < 5) {
        refs.current[i + 1]?.focus();
      }
    };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const copy = Array(6).fill("");
    for (let i = 0; i < text.length; i++) copy[i] = text[i];
    onChange(copy);
    refs.current[Math.min(text.length, 5)]?.focus();
  };

  return (
    <div className="flex gap-2 justify-between" onPaste={onPaste}>
      {value.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={d}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={onKeyDown(i)}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl tabular-nums font-bold text-[#0f172a] bg-white border-[1.5px] border-[#dbe2ec] rounded-md focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-primary)_15%,transparent)] transition-all disabled:opacity-60"
        />
      ))}
    </div>
  );
}
